/**
 * TikTok 自定义颜色值的中文 → 英文映射。
 *
 * 背景：`Get Category Attributes` 对部分类目把颜色返回为
 * `SALES_PROPERTY + is_customizable=true + values: []`，即平台不下发枚举、值名由商家提供。
 * 站内发品页面因此默认用中文色名（运营侧更直观、与货号/图片素材一致），
 * 而 TikTok 属跨境 C 端展示，自定义值名需要英文，故提交前在此统一转英文。
 *
 * 规则：
 * - 命中映射表 → 英文（先按原名，再按去掉结尾「色」的写法，再按单字别名）；
 * - 未命中且仍含中日韩字符 → 空串（平台不接受中文，调用方须拦截）；
 * - 已是拉丁文/数字等 → 原样返回；
 * - 平台下发的内置值（有 value_id）不走本映射，避免与平台值名不一致。
 */

import { fixedUi } from '@/i18n/fixedUi';

/** 常用中文色名 → 英文（覆盖服装/家居常见色，含深浅与特殊色） */
export const TIKTOK_COLOR_EN_BY_CN = {
  黑色: 'Black',
  白色: 'White',
  米白: 'Cream',
  米色: 'Beige',
  灰色: 'Gray',
  深灰: 'Dark Gray',
  浅灰: 'Light Gray',
  红色: 'Red',
  酒红: 'Wine Red',
  砖红: 'Brick Red',
  粉色: 'Pink',
  藕粉: 'Dusty Pink',
  玫红: 'Rose Red',
  蓝色: 'Blue',
  浅蓝: 'Light Blue',
  深蓝: 'Dark Blue',
  藏青: 'Navy',
  牛仔蓝: 'Denim Blue',
  雾霾蓝: 'Haze Blue',
  天蓝: 'Sky Blue',
  绿色: 'Green',
  墨绿: 'Dark Green',
  军绿: 'Army Green',
  浅绿: 'Light Green',
  薄荷绿: 'Mint Green',
  黄色: 'Yellow',
  鹅黄: 'Light Yellow',
  杏色: 'Apricot',
  橙色: 'Orange',
  橘色: 'Orange',
  紫色: 'Purple',
  香芋紫: 'Taro Purple',
  棕色: 'Brown',
  咖啡色: 'Coffee',
  咖色: 'Coffee',
  卡其: 'Khaki',
  驼色: 'Camel',
  银色: 'Silver',
  金色: 'Gold',
  香槟色: 'Champagne',
  铜色: 'Bronze',
  肤色: 'Nude',
  透明: 'Transparent',
  多色: 'Multicolor',
  花色: 'Multicolor',
  拼色: 'Multicolor',
};

/** 单字别名（「黑」等价「黑色」） */
const CN_SHORT_ALIASES = {
  黑: '黑色',
  白: '白色',
  灰: '灰色',
  红: '红色',
  粉: '粉色',
  蓝: '蓝色',
  绿: '绿色',
  黄: '黄色',
  橙: '橙色',
  紫: '紫色',
  棕: '棕色',
  咖: '咖啡色',
};

const CUSTOM_COLOR_CJK_MESSAGE = '自定义颜色须为英文（平台不接受中文）。常见色名会自动转英文，其余请改用英文填写';

function colorNameMessage(zh) {
  return fixedUi(zh);
}

/** 归一化：去空白/全角空格，压缩重复空格 */
function normalizeLabel(raw) {
  return String(raw ?? '')
    .replace(/[\u3000\u00a0]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** 是否含中日韩统一表意文字（自定义色提交禁用） */
export function hasCjkScript(raw) {
  return /[\u3400-\u9FFF\uF900-\uFAFF]/.test(String(raw || ''));
}

/**
 * 仅做已知中文色名映射；未命中返回空串（不回退原文）。
 */
function mapKnownCnColorName(text) {
  const compact = text.replace(/\s+/g, '');
  const lookup = (key) => TIKTOK_COLOR_EN_BY_CN[key] || '';
  const direct = lookup(text) || lookup(compact);
  if (direct) return direct;
  // 「藏青色」→「藏青」
  if (compact.length > 1 && compact.endsWith('色')) {
    const base = compact.slice(0, -1);
    if (lookup(base)) return lookup(base);
    if (CN_SHORT_ALIASES[base]) return lookup(CN_SHORT_ALIASES[base]);
  }
  // 「黑」→「黑色」
  if (CN_SHORT_ALIASES[compact]) return lookup(CN_SHORT_ALIASES[compact]);
  // 「黑色拼白色」等组合色：逐段翻译后拼接
  const parts = compact.split(/[\/、,，+&]|(?:拼|配|和)/).filter(Boolean);
  if (parts.length > 1) {
    const mapped = parts.map((p) => {
      if (lookup(p)) return lookup(p);
      if (p.length > 1 && p.endsWith('色') && lookup(p.slice(0, -1))) {
        return lookup(p.slice(0, -1));
      }
      if (CN_SHORT_ALIASES[p]) return lookup(CN_SHORT_ALIASES[p]);
      return '';
    });
    if (mapped.every(Boolean) && new Set(mapped).size > 1) return mapped.join('/');
  }
  return '';
}

/**
 * 中文自定义色名 → 提交给 TikTok 的英文色名。
 * 未命中映射表且仍含中文时返回空串（平台不接受）。
 */
export function toTiktokSubmitColorName(label) {
  const text = normalizeLabel(label);
  if (!text) return '';
  const mapped = mapKnownCnColorName(text);
  if (mapped) return mapped;
  if (hasCjkScript(text)) return '';
  return text;
}

/** 是否命中中文映射（便于 UI 提示「提交时会自动转英文」） */
export function hasTiktokColorCnMapping(label) {
  const text = normalizeLabel(label);
  return Boolean(text && mapKnownCnColorName(text));
}

/**
 * 自定义颜色录入/提交前校验：映射已知中文，拒绝仍含中文的自造色名。
 * @returns {{ ok: boolean, name: string, message: string }}
 */
export function assertLatinCustomColorName(label) {
  const text = normalizeLabel(label);
  if (!text) {
    return { ok: false, name: '', message: colorNameMessage('请输入颜色名称') };
  }
  const name = toTiktokSubmitColorName(text);
  if (!name) {
    return { ok: false, name: '', message: colorNameMessage(CUSTOM_COLOR_CJK_MESSAGE) };
  }
  return { ok: true, name, message: '' };
}

export default {
  TIKTOK_COLOR_EN_BY_CN,
  hasCjkScript,
  toTiktokSubmitColorName,
  hasTiktokColorCnMapping,
  assertLatinCustomColorName,
};
